import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Users, Search, Download, Trash2, Calendar, Phone, Send as TelegramSend } from 'lucide-react';
import { toast } from 'sonner';
import { adminMe, API, getAdminToken, listTenants } from '../../../lib/api';

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso; }
}

export default function UsersSection() {
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [filterTid, setFilterTid] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const m = await adminMe();
      setMe(m);
      const isSuper = m.role === 'super_admin';
      if (isSuper) {
        try { setTenants(await listTenants()); } catch {}
      }
      const url = `${API}/admin/users${isSuper && filterTid ? `?tenant_id=${encodeURIComponent(filterTid)}` : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${getAdminToken()}` , 'x-tenant-host': window.location.hostname } });
      const data = await res.json();
      setUsers(data.users || []);
    } catch (e) {
      toast.error('Failed to load users');
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filterTid]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (
      (u.name || '').toLowerCase().includes(q) ||
      (u.mobile || '').toLowerCase().includes(q) ||
      String(u.tg_chat_id || '').includes(q) ||
      (u.tg_username || '').toLowerCase().includes(q)
    ));
  }, [users, search]);

  const downloadCsv = async () => {
    try {
      const url = `${API}/admin/users/export${me?.role === 'super_admin' && filterTid ? `?tenant_id=${encodeURIComponent(filterTid)}` : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${getAdminToken()}`, 'x-tenant-host': window.location.hostname } });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl; a.download = `users-${Date.now()}.csv`; a.click();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Downloaded users.csv');
    } catch (e) { toast.error('Download failed'); }
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`Delete user ${user.name} (${user.mobile})? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/admin/users/${user.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAdminToken()}`, 'x-tenant-host': window.location.hostname }
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Delete failed');
      }
      toast.success('User deleted');
      reload();
    } catch (e) { toast.error(e.message || 'Failed to delete'); }
  };

  const isSuper = me?.role === 'super_admin';

  return (
    <div className="space-y-6" data-testid="users-section">
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <CardTitle className="text-zinc-100 flex items-center gap-2"><Users size={18} /> Registered Users <span className="text-zinc-500 text-sm font-normal">({filtered.length})</span></CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {isSuper && (
              <select value={filterTid} onChange={(e) => setFilterTid(e.target.value)} className="h-9 px-3 rounded-md bg-zinc-950 border border-zinc-800 text-zinc-100 text-sm" data-testid="users-tenant-filter">
                <option value="">All Tenants</option>
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            )}
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, mobile, Telegram…" className="h-9 pl-8 bg-zinc-950 border-zinc-800 text-zinc-100 w-64" data-testid="users-search-input" />
            </div>
            <Button onClick={downloadCsv} className="bg-[color:var(--gold)] text-black hover:brightness-110 h-9" data-testid="users-download-btn">
              <Download size={14} className="mr-1" /> CSV
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="text-zinc-500 text-sm p-6">Loading users…</div>
          ) : filtered.length === 0 ? (
            <div className="text-zinc-500 text-sm p-6">No users to show.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead className="text-zinc-500 text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left py-2 px-2"><span className="inline-flex items-center gap-1"><Calendar size={11} /> Joined</span></th>
                    <th className="text-left py-2 px-2">Name</th>
                    <th className="text-left py-2 px-2"><span className="inline-flex items-center gap-1"><Phone size={11} /> Mobile</span></th>
                    <th className="text-left py-2 px-2"><span className="inline-flex items-center gap-1"><TelegramSend size={11} /> Telegram</span></th>
                    {isSuper && <th className="text-left py-2 px-2">Tenant</th>}
                    <th className="text-right py-2 px-2">Action</th>
                  </tr>
                </thead>
                <tbody className="text-zinc-200">
                  {filtered.map((u) => (
                    <tr key={u.id} className="border-t border-zinc-800/70 hover:bg-zinc-950/60" data-testid={`user-row-${u.id}`}>
                      <td className="py-2 px-2 text-zinc-400 whitespace-nowrap">{fmtDate(u.created_at)}</td>
                      <td className="py-2 px-2 font-semibold">{u.name || '—'}</td>
                      <td className="py-2 px-2 tabular-nums">+{u.mobile}</td>
                      <td className="py-2 px-2 text-zinc-400">
                        {u.tg_chat_id ? (
                          <div className="flex flex-col leading-tight">
                            <span className="text-xs">{u.tg_username ? `@${u.tg_username}` : 'verified'}</span>
                            <span className="text-[10px] text-zinc-600 tabular-nums">ID: {u.tg_chat_id}</span>
                          </div>
                        ) : <span className="text-zinc-600 text-xs">—</span>}
                      </td>
                      {isSuper && <td className="py-2 px-2 text-zinc-500 text-xs">{u.tenant_name || u.tenant_id || '—'}</td>}
                      <td className="py-2 px-2 text-right">
                        <button onClick={() => deleteUser(u)} className="text-red-400 hover:text-red-300 p-1" data-testid={`user-delete-${u.id}`} aria-label="Delete user">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
